<script setup lang="ts">
import { useStore } from 'vuex';
import { MutationTypes } from '../store';
import { offTrackCompleteRateEvent, onTrackCompleteRateEvent } from '../utils/event-bus';
import trackEvent, { GEM_METER_EVENT_ID } from '@/modules/common/utils/tracker';
import ProgressStatus from './ProgressStatus.vue';
import useGemMeter from '../composable/useGemMeter';
import type { GemMeterMetric } from '../types';
import { ActionTypes } from '../store/actions';
import { MODULE_NAME } from '../constants/moduleConstants';
let flagGetImage = false;
const GemMeter = defineAsyncComponent(() => import('./GemMeter.vue'));

const store = useStore();
const { cancelModal } = useGemMeter();

const toggleGemMeter = ref(false);
const pageType = computed(() => store.getters['app/pageType']);
const isBlankPage = computed(() => store.state.editor.isBlankPage);
const gemMeterOBD = computed(() => store.state.app.shopMeter.onboarding);
const openModal = computed(() => store.state.gemMeter.openModal);
const hasSubPageAndNotBlankPage = computed(() => pageType.value === 'template-page' && !isBlankPage.value);
watch(isBlankPage, (newValue) => {
  if (newValue && store.state.gemMeter.openModal) {
    store.commit(`gemMeter/${MutationTypes.CHANGE_MODAL_VISIBILITY}`, false);
  }
});

watch(
  hasSubPageAndNotBlankPage,
  (newValue) => {
    toggleGemMeter.value = newValue;
  },
  { immediate: true },
);

onMounted(() => {
  onTrackCompleteRateEvent((action) => {
    if (!store.state.app.shopMeter.onboarding) {
      return;
    }

    const { lg, xs } = store.state.gemMeter.metric as GemMeterMetric;
    const desktopCompleteRate = ((lg.fixed + lg.skipped) / (lg.fixed + lg.skipped + lg.errors)) * 100;
    const mobileCompleteRate = ((xs.fixed + xs.skipped) / (xs.fixed + xs.fixed + xs.errors)) * 100;
    trackEvent({
      eventId: GEM_METER_EVENT_ID.COMPLETE_RATE,
      properties: {
        action,
        desktop_complete_rate: parseInt(desktopCompleteRate.toFixed(0), 10),
        desktop_error_left_count: lg.errors,
        desktop_skip_error_count: lg.skipped,
        desktop_fixed_count: lg.fixed,
        mobile_complete_rate: parseInt(mobileCompleteRate.toFixed(0), 10),
        mobile_error_left_count: xs.errors,
        mobile_skip_error_count: xs.skipped,
        mobile_fixed_count: xs.fixed,
      },
    });
  });
  if (!flagGetImage) {
    flagGetImage = true;
    setTimeout(() => {
      store.dispatch(`${MODULE_NAME}/${ActionTypes.GET_IMAGES}`);
    }, 1000);
  }
});

onUnmounted(() => {
  offTrackCompleteRateEvent();
});
</script>

<template>
  <div v-if="toggleGemMeter" class="gp-meter">
    <div v-if="openModal && !gemMeterOBD" class="gp-meter__overlay" @click="cancelModal" />
    <ProgressStatus />
  </div>
  <GemMeter :is-draggable="true" />
</template>

<style lang="scss">
html {
  overflow: hidden;
}

.gp-meter {
  &__overlay {
    width: 100vw;
    height: 100vh;
    position: absolute;
    left: 0;
    top: 0;
    background: rgba(0, 0, 0, 0.15);
    z-index: 99999;
    overflow: hidden;
  }
}

$cTool: #fbb144;
.gf-element-active {
  &.gfFontSelect {
    display: inline-flex;
    flex-direction: column;
    width: 100%;
  }
}
.gf-element-active {
  position: relative;
  cursor: text;

  &:after {
    content: '';
    position: absolute;
    top: -5px;
    left: -5px;
    bottom: 0px;
    right: -5px;
    border: 1px $cTool solid;
    border: 1px solid $cTool;
    box-shadow: 0 0 3px $cTool;
    z-index: -1;
    animation: fadeOut 1.5s;
  }

  @keyframes fadeOut {
    0% {
      background-color: rgba(202, 148, 15, 0.2);
      box-shadow: 0px 0px 14px #fdb913;
    }

    100% {
      background-color: transparent;
      box-shadow: none;
    }
  }
}
</style>
